import {
  CellStyleModule,
  ClientSideRowModelModule,
  ColumnApiModule,
  RowApiModule,
} from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'

import { QueryLoadingManager } from '@b-stock/fe-core/bstock-next/components'
import { manifestPivotTableQuery } from '@b-stock/fe-core/bstock-next/queries'
import type { ManifestPivotRow } from '@b-stock/order-process-api-client'

import {
  registerAgGridModules,
  useAgGridLicenseReady,
} from '@/hooks/ag-grid/agGridSetup'

import { GridContainer, gridTheme, SCROLLBAR_WIDTH } from '../ui'
import { defaultPivotColDef, usePivotColumnDefs } from './usePivotColumnDefs'
import { isEmptyPivotTable, PivotTableEmptyState } from './utils'
import type { PivotTableKey } from './utils'

registerAgGridModules([
  CellStyleModule,
  ClientSideRowModelModule,
  ColumnApiModule,
  RowApiModule,
])

export const ManifestPivotTableAgGrid = ({
  currency,
  lotId,
  pivotTableKey,
}: {
  currency?: string
  lotId: string
  pivotTableKey: PivotTableKey
}) => {
  const query = manifestPivotTableQuery.useQuery({
    lotId,
    by: pivotTableKey,
  })

  return (
    <QueryLoadingManager {...query} showLoadingComponent={false}>
      {({ data }) => {
        const pivotData = data?.manifestPivotData ?? []
        if (data && isEmptyPivotTable(pivotTableKey, pivotData)) {
          return <PivotTableEmptyState pivotTableKey={pivotTableKey} />
        }
        return (
          <PivotGrid
            currency={currency}
            isPending={query.status === 'pending'}
            pivotData={pivotData}
            pivotTableKey={pivotTableKey}
          />
        )
      }}
    </QueryLoadingManager>
  )
}

const PivotGrid = ({
  currency,
  isPending,
  pivotData,
  pivotTableKey,
}: {
  currency?: string
  isPending: boolean
  pivotData: Partial<ManifestPivotRow>[]
  pivotTableKey: PivotTableKey
}) => {
  const {
    subjectColumns,
    quantityColumn: qtyColumn,
    extMsrpColumn,
  } = usePivotColumnDefs({ currency })
  const isAgGridReady = useAgGridLicenseReady()

  const hasQty = pivotData.some((row) => row.sumQuantity)
  const hasExtMsrp = pivotData.some(
    (row) => row.sumQuantityTimesAttributesUnitRetail
  )

  const columnDefs = [
    {
      ...subjectColumns[pivotTableKey],
      flex: 1,
    },
    ...(hasQty ? [qtyColumn] : []),
    ...(hasExtMsrp ? [extMsrpColumn] : []),
  ]

  // Wait for the AG Grid Enterprise license to be registered before mounting
  // the grid — mounting earlier races LicenseManager.setLicenseKey and can
  // log a spurious "no license" console error.
  if (!isAgGridReady) {
    return <GridContainer />
  }

  return (
    <GridContainer>
      <AgGridReact
        columnDefs={columnDefs}
        defaultColDef={defaultPivotColDef}
        domLayout="autoHeight"
        loading={isPending}
        scrollbarWidth={SCROLLBAR_WIDTH}
        rowData={pivotData}
        rowModelType="clientSide"
        theme={gridTheme}
      />
    </GridContainer>
  )
}
